import React, { useCallback } from 'react';
import { FormGroup, TextArea } from '~/components/form';
import { CgNotes } from 'react-icons/cg';
import { useForm } from '~/hooks';
import { useSectionLoader, useAutoSave, useDeleteEntireSectionDetail } from '../../hooks';
import sectionTypes from '../sectionTypes';
import RecordedInBook from './RecordedInBook';
import { SectionHeader } from '../../../PolicySearchRequest/common';

/**
 * @typedef RestrictiveCovenantsValues
 * @property {number} sectionId
 * @property {string} restrictiveCovenants
 * @property {string} forfeiture
 * @property {string} violation
 * @property {[object]} initialDefaultData
 * @typedef FormState
 * @property {RestrictiveCovenantsValuesValues} values The current form values
 */

/** @type {RestrictiveCovenantsValuesValues} */
const defaultState = {
    sectionId: 0,
    orderNumber: null,
    restrictiveCovenants: 'Yes',
    forfeiture: 'No',
    violation: 'No',
    initialDefaultData: {
        restrictiveCovenants: 'Yes',
        forfeiture: 'No',
        violation: 'No',
        feetFromFront: '',
        feetFromRear: '',
        feetFromSide: '',
        feetFromSideStreet: '',
        easementsOthers: '',
        reportAttached: 'No'
    }
};



const RestrictiveCovenants = ({ titleOpinionId, readOnly }) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.RESTRICTIVE_COVENANTS, titleOpinionId);

    const onLoad = useCallback((values) => {
        return loadSection(values);
    }, [titleOpinionId]);

    const {
        /** @type {FormState} */
        state,
        fieldActions
    } = useForm({
        /** @type {RestrictiveCovenantsValuesValues} */
        initialState: { ...defaultState },
        onLoad,
        debug: true
    });

    const { saveField } = useAutoSave(sectionTypes.RESTRICTIVE_COVENANTS, titleOpinionId, state.values.initialDefaultData, state.values.sectionId);

    const { deleteEntireSectionDetail } = useDeleteEntireSectionDetail();

    const onBlur = (value, path, validator) => {
        saveField(value, path, state.values.initialDefaultData, state.values.sectionId);
        return fieldActions.onBlur(value, path, validator);
    };

    const onChangeHandler = async (event) => {
        await saveField(event.target.value, event.target.name, state.values.initialDefaultData, state.values.sectionId);
        if (event.target.name === 'restrictiveCovenants' && event.target.value != 'Yes' && state.values.sectionDetails && state.values.sectionDetails.length !== 0) {
            await deleteEntireSectionDetail(state.values.sectionDetails);
        }
        return fieldActions.onChange(event.target.value, event.target.name);

    };


    return (
        <div className="ml-5 mr-5">
            <div className="mb-5">
                <div>
                    <div className="z-10 flex items-center h-full text-xl font-semibold text-blue-60">
                        <div className="h-11 w-11 rounded-md flex items-center justify-center bg-blue-30"><CgNotes size="45%" /></div>
                        <div className="ml-2"> Restrictive Covenants and Conditions </div>
                    </div>

                </div>
            </div>
            <div className="text-sm mb-4">Please note any information changes will overwrite any previously entered. Changes are saved automatically.</div>
            <div className="col-span-8 grid grid-cols-12 grid-flow-row gap-x-8 gap-y-4 text-sm bold mb-4 mt-4">

                <div className="col-span-12 flex flex-direction:row gap-x-5 gap-y-4">
                    <p className="font-medium">
                        Are there any restrictive covenants and conditions ?
                    </p>
                    <input type="radio" value="Yes" name="restrictiveCovenants"
                        checked={state.values.restrictiveCovenants === 'Yes'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}

                    /> Yes
                    <input type="radio" value="No" name="restrictiveCovenants"
                        checked={state.values.restrictiveCovenants === 'No'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> No
                    <input type="radio" value="None" name="restrictiveCovenants"
                        checked={state.values.restrictiveCovenants === 'None'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> None Since Prior Policy
                </div>

                {state.values.restrictiveCovenants === 'Yes' ?
                    <div className="grid grid-cols-12 grid-flow-row col-span-12 gap-x-3">
                        <p className="font-semibold gap-y-10 mb-5">

                        </p>
                        {/* Current Owner(s) */}
                        <SectionHeader colSpan={12}> Recorded In</SectionHeader>
                        <RecordedInBook colSpan={12} fieldActions={fieldActions} titleOpinionId={titleOpinionId} sectionId={state.values.sectionId} sectionDetails={state.values.sectionDetails} initialDefaultData={state.values.initialDefaultData} editMode={!readOnly} />

                        <div className="col-span-12 flex flex-direction:row gap-x-5 gap-y-7 mb-5 mt-2">
                            <p className="font-medium text-neutral-90">
                                Does survey and/or public record indicate a violation?
                            </p>
                            <input type="radio" value="Yes" name="violation"
                                checked={state.values.violation === 'Yes'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                            /> Yes
                            <input type="radio" value="No" name="violation"
                                checked={state.values.violation === 'No'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                            /> No
                            <input type="radio" value="Unknown" name="violation"
                                checked={state.values.violation === 'Unknown'}
                                disabled={readOnly}
                                onChange={onChangeHandler}
                            /> Unknown
                        </div>

                        <div className="col-span-12 flex flex-direction:row gap-x-5 gap-y-4 mb-5 mt-2">
                            <p className="font-medium text-neutral-90">
                                Contain a reversionary or forfeiture clause?
                            </p>
                            <input type="radio" value="Yes" name="forfeiture"
                                checked={state.values.forfeiture === 'Yes'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                            /> Yes
                            <input type="radio" value="No" name="forfeiture"
                                checked={state.values.forfeiture === 'No'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                            /> No
                            <input type="radio" value="Unknown" name="forfeiture"
                                checked={state.values.forfeiture === 'Unknown'}
                                disabled={readOnly}
                                onChange={onChangeHandler}
                            /> Unknown
                        </div>
                        <h5 className="col-span-12 font-semibold mb-2 mt-2"> Building Setback Lines </h5>
                        <FormGroup
                            label="Feet from Front"
                            groupClassName="col-span-3 mb-2"
                            path='feetFromFront'
                            readOnly={readOnly}
                            value={state.values.feetFromFront}
                            error={state.errors.feetFromFront}
                            {...fieldActions}
                            onBlur={onBlur}
                        />
                        <FormGroup
                            label="Feet from Side"
                            groupClassName="col-span-3 mb-2"
                            path='feetFromSide'
                            readOnly={readOnly}
                            value={state.values.feetFromSide}
                            error={state.errors.feetFromSide}
                            {...fieldActions}
                            onBlur={onBlur}
                        />
                        <FormGroup
                            label="Feet from Side Street"
                            groupClassName="col-span-3 mb-2"
                            path='feetFromSideStreet'
                            readOnly={readOnly}
                            value={state.values.feetFromSideStreet}
                            error={state.errors.feetFromSideStreet}
                            {...fieldActions}
                            onBlur={onBlur}
                        />
                        <FormGroup
                            label="Feet from Rear"
                            groupClassName="col-span-3 mb-2"
                            path='feetFromRear'
                            readOnly={readOnly}
                            value={state.values.feetFromRear}
                            error={state.errors.feetFromRear}
                            {...fieldActions}
                            onBlur={onBlur}
                        />

                        <TextArea
                            type="textarea"
                            label="Easements/ Other Matters as shown in covenants"
                            name="easementsOthers"
                            path='easementsOthers'
                            readOnly={readOnly}
                            groupClassName="col-span-12 mb-2 mt-2"
                            value={state.values.easementsOthers}
                            error={state.errors.easementsOthers}
                            {...fieldActions}
                            onChange={(easementsOthers) => {
                                saveField(easementsOthers, 'easementsOthers', state.values.initialDefaultData);
                                fieldActions.onChange(easementsOthers, 'easementsOthers');
                            }}
                            onBlur={onBlur}
                        />
                    </div>
                    :
                    <div></div>
                }

                <div className="col-span-12 flex flex-direction:row gap-x-5 gap-y-4">
                    <p className="font-medium text-neutral-90">
                        Survey &amp; Inspection Report Attached?
                    </p>
                    <input type="radio" value="Yes" name="reportAttached"
                        checked={state.values.reportAttached === 'Yes'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> Yes
                    <input type="radio" value="No" name="reportAttached"
                        checked={state.values.reportAttached === 'No'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> No
                </div>
            </div>
        </div>
    );
};

export default RestrictiveCovenants;